import { Theme } from '@mui/material/styles';
import { themeColors } from './theme';

// Custom styles for the SubmitGarden component
export const gardenStyles = (theme: Theme) => ({
  pageContainer: {
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    padding: 0,
    margin: 0,
    paddingTop: '14vh', // Space for the fixed header
  },
  headerBanner: {
    background: themeColors.darkBackground,
    padding: theme.spacing(6, 0),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 30px rgba(0,0,0,0.2)',
    zIndex: 10,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 0),
    },
  },
  headerTitle: {
    fontFamily: '"Darumadrop One", cursive',
    color: '#fff',
    fontSize: '4rem',
    position: 'relative',
    zIndex: 3,
    textAlign: 'center',
    textShadow: '3px 3px 0px rgba(0,0,0,0.2)',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2.5rem',
    },
  },
  pointillistOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `radial-gradient(${themeColors.mainYellow}33 10%, transparent 10.5%), 
                      radial-gradient(${themeColors.mainYellow}33 10%, transparent 10.5%)`,
    backgroundSize: '30px 30px',
    backgroundPosition: '0 0, 15px 15px',
    opacity: 0.6,
    zIndex: 2,
  },
  gradientBackground: {
    background: `linear-gradient(135deg, ${themeColors.mainYellow} 0%, ${themeColors.orange} 100%)`,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.8,
    zIndex: 1,
  },
  contentContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    mt: 3,
    padding: theme.spacing(4, 3),
    position: 'relative',
    zIndex: 5,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 2),
    },
  },
  introText: {
    fontFamily: 'Roboto, sans-serif',
    color: themeColors.darkBackground,
    fontSize: '1.25rem',
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto 40px',
    lineHeight: 1.6,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(16px)',
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 15px rgba(0,0,0,0.07)',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
      padding: theme.spacing(2),
      margin: '0 auto 24px',
    },
  },
  card: {
    borderRadius: theme.spacing(3),
    backgroundColor: themeColors.red,
    boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
    padding: theme.spacing(4),
    position: 'relative',
    overflow: 'hidden',
    border: 'none',
    backdropFilter: 'blur(16px)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 2),
      marginBottom: theme.spacing(3),
    },
  },
  stepTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: themeColors.darkBackground,
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'DM Sans", sans-serif',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  stepIcon: {
    color: 'black',
    marginRight: theme.spacing(2),
    fontSize: '2rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
      marginRight: theme.spacing(1),
    },
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      borderRadius: '40px',
      '&:hover fieldset': {
        borderColor: themeColors.orange,
      },
      '&.Mui-focused fieldset': {
        borderColor: themeColors.orange,
        borderWidth: 2,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: themeColors.orange,
      fontWeight: 500,
    },
  },
  pubCard: {
    background: 'rgba(231, 248, 253, 0.2)',
    backdropFilter: 'blur(16px)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    borderLeft: `6px solid ${themeColors.blue}`,
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
  },
  mapContainer: {
    borderRadius: theme.spacing(3),
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    marginBottom: theme.spacing(3),
    border: `2px solid ${themeColors.blue}`,
    height: '600px',
    width: '100%',
    position: 'relative',
    '& .map-container': {
      width: '100%',
      height: '100%',
    },
    '& .gm-style-mtc button, & .gm-fullscreen-control, & .gm-svpc, & .gm-style-zoom-in, & .gm-style-zoom-out': {
      background: 'rgba(255, 255, 255, 0.9) !important',
      padding: '8px !important',
      borderRadius: '4px !important',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3) !important',
      margin: '2px !important',
    },
    '& .gm-style-mtc div[role="menu"]': {
      borderRadius: '8px !important',
      overflow: 'hidden !important',
      marginTop: '2px !important',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2) !important',
    },
    [theme.breakpoints.down('sm')]: {
      height: '450px',
      borderRadius: theme.spacing(2),
    },
  },
  gardenNameSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(2),
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
  },
  googleToggle: {
    color: themeColors.blue,
    fontSize: '0.9rem',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'rgba(60, 104, 169, 0.12)',
    },
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
  },
  rightButtonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'space-between',
    },
  },
  buttonPrimary: {
    background: themeColors.orange,
    color: '#fff',
    fontWeight: 600,
    padding: theme.spacing(1.5, 4),
    borderRadius: theme.spacing(2),
    border: 'none',
    boxShadow: '0 4px 10px rgba(252, 82, 8, 0.3)',
    fontSize: '1rem',
    '&:hover': {
      background: `linear-gradient(90deg, ${themeColors.orange} 0%, ${themeColors.mainYellow} 100%)`,
      boxShadow: '0 6px 14px rgba(252, 82, 8, 0.4)',
    },
    '&:disabled': {
      background: '#E0E0E0',
      color: '#9E9E9E',
      boxShadow: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: theme.spacing(1.2, 3),
    },
  },
  buttonSecondary: {
    color: themeColors.darkBackground,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderColor: themeColors.darkBackground,
    borderWidth: 2,
    borderRadius: theme.spacing(2),
    fontWeight: 500,
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      borderWidth: 2,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: theme.spacing(1.2, 3),
    },
  },
  buttonDanger: {
    color: '#fff',
    backgroundColor: '#D32F2F',
    borderRadius: theme.spacing(2),
    fontWeight: 500,
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    '&:hover': {
      backgroundColor: '#B71C1C',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: theme.spacing(1.2, 3),
    },
  },
  gardenButtonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  gardenButton: {
    margin: theme.spacing(0.5),
    borderRadius: theme.spacing(1.5),
    backgroundColor: 'transparent',
    color: themeColors.blue,
    borderColor: themeColors.blue,
    borderWidth: 2,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'rgba(60, 104, 169, 0.08)',
      borderWidth: 2,
    },
  },
  gardenButtonSelected: {
    backgroundColor: themeColors.blue,
    color: '#fff',
    borderWidth: 2,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: themeColors.blue,
      borderWidth: 2,
    },
  },
  customNameField: {
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(2),
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
  },
  instructionBox: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    backgroundColor: 'rgba(251, 189, 32, 0.15)',
    borderLeft: `6px solid ${themeColors.mainYellow}`,
    color: themeColors.darkBackground,
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  instructions: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    backgroundColor: 'rgba(251, 189, 32, 0.15)',
    borderLeft: `6px solid ${themeColors.mainYellow}`,
    color: themeColors.darkBackground,
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  pubName: {
    fontWeight: 600,
    fontSize: '1.2rem',
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    color: themeColors.darkBackground,
  },
  pubAddress: {
    fontSize: '0.9rem',
    marginBottom: theme.spacing(1),
    color: 'text.secondary',
  },
  pubCoords: {
    fontSize: '0.9rem',
    color: 'text.secondary',
    fontFamily: 'monospace',
  },
  viewerMessage: {
    flex: 1,
    maxWidth: '800px',
  },
  viewerPaper: {
    height: '70vh',
    borderRadius: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(16px)',
    overflow: 'hidden',
    boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
  },
  dialogTitle: {
    background: themeColors.darkBackground,
    color: '#fff',
    padding: theme.spacing(3, 3, 2),
    fontFamily: '"Darumadrop One", cursive',
    fontSize: '1.5rem',
  },
  dialogContent: {
    padding: theme.spacing(4, 3, 3),
  },
  dialogActions: {
    padding: theme.spacing(2, 3, 3),
  },
  alertSuccess: {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: '#2E7D32',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  alertInfo: {
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
    color: '#0277BD',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  existingGardensTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    color: themeColors.darkBackground,
    fontSize: '1.25rem',
  },
  existingGardensIcon: {
    color: themeColors.blue,
    marginRight: theme.spacing(1.5),
    fontSize: '1.5rem',
  },
}); 